import {
  useCallback,
  type ChangeEventHandler,
  type AriaAttributes,
} from 'react'

import { Icon } from '@/components/Icon'
import { Skeleton } from '@/components/Skeleton'
import styles from './Radio.module.css'
import cx from 'classnames'

export interface RadioProps extends AriaAttributes {
  /**
   * The id of the radio input.
   */
  id?: string
  /**
   * The name of the radio input.
   */
  name?: string
  /**
   * The value of the radio input.
   */
  value?: string
  /**
   * Whether the radio is checked.
   */
  checked?: boolean
  /**
   * Whether the radio is disabled.
   */
  disabled?: boolean
  /**
   * Whether the radio is a skeleton.
   */
  isSkeleton?: boolean
  /**
   * Whether the radio is invalid.
   */
  invalid?: boolean
  /**
   * Whether to add icon-like padding to the radio.
   */
  isPadded?: boolean
  /**
   * Whether the radio is read-only.
   */
  readOnly?: boolean
  /**
   * Visual variant of the radio when checked.
   */
  variant?: 'default' | 'check'
  /**
   * The callback function that is called when the checked state is changed.
   */
  onChange?: (value: string) => unknown
  /**
   * Test ID attribute.
   */
  testId?: string
}

export function Radio({
  id,
  name,
  value,
  checked,
  disabled,
  isSkeleton,
  invalid: _,
  isPadded,
  onChange,
  readOnly,
  variant,
  testId,
  ...rest
}: RadioProps) {
  const change: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onChange?.(e.target.value)
    },
    [onChange],
  )

  return (
    <div
      className={cx(
        styles.radio,
        disabled && styles.disabled,
        isPadded && styles.padding,
      )}
    >
      <input
        id={id}
        name={name}
        value={value}
        type="radio"
        checked={checked}
        readOnly={readOnly}
        onChange={onChange ? change : undefined}
        disabled={disabled}
        className={styles.input}
        data-test={testId}
        {...rest}
      />
      <div className={styles.default}>
        <Icon name="radio-default" />
      </div>
      <div className={styles.checked}>
        <Icon name={variant === 'check' ? 'radio-check' : 'radio-selected'} />
      </div>

      {isSkeleton && (
        <div className={styles.skeleton}>
          <Skeleton
            variant="circle"
            height="var(--box-size)"
            width="var(--box-size)"
            block
          />
        </div>
      )}
    </div>
  )
}
